import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
  type SVGAttributes,
} from "react";
import { createPortal } from "react-dom";

import { Box, Column, IconButton, Tooltip } from "@hightouch/htui";
import { useReducedMotion } from "motion/react";
import { useHotkeys } from "react-hotkeys-hook";

import type { AgentActivity } from "src/components/llm/embedded-chat/types";
import { ActiveMarkerOverlay } from "src/components/active-marker-icon";
import { AgentSidebarCollapseButton } from "src/components/layout/page-sidebar/agent/agent-sidebar-collapse-button";
import { ChatTitleButtonSizeProvider } from "src/components/llm/context/chat-title-button-size-context";
import { useHeaderHeight } from "src/contexts/header-height-context";
import { useIsScreenAtLeast } from "src/hooks/use-is-large-screen";
import { PageSidebarContainer } from "src/components/layout/page-sidebar/page-sidebar-container";
import { useViewInset } from "src/components/layout/view-height";
import type { SidebarName } from "src/components/layout/page-sidebar/constants";
import {
  BOTTOM_BAR_HEIGHT_PX,
  WIZARD_HEADER_SIZE_PX,
} from "src/components/wizard/resource-wizard";
import { useLocation, useSearchParams } from "src/router";
import {
  CrumbBarEndSlotContext,
  SIDEBAR_TOGGLE_PROPS,
  useIsMiniNavCollapsed,
} from "src/components/layout/nav/mini-nav/mini-nav-context";
import { navMotion } from "src/components/layout/nav/nav-motion";

import {
  useAgentSidebarState,
  type AgentSidebarState,
} from "src/components/layout/page-sidebar/agent/use-agent-sidebar-state";
import {
  AGENT_SESSION_PARAM,
  withAgentSessionParam,
} from "src/components/llm/embedded-chat/agent-session-search-params";
import {
  AgentAwaitingReplyMarker,
  AgentWorkingShimmer,
  CollapsedRailButton,
} from "src/components/layout/page-sidebar/agent/collapsed-rail-button";
import {
  AGENT_SIDEBAR_CLIP_PADDING_PX as CLIP_PADDING_PX,
  AGENT_SIDEBAR_DOCK_BREAKPOINT,
  AGENT_SIDEBAR_EDGE_GAP_PX,
  AGENT_SIDEBAR_OVERLAY_CONTENT_GAP_PX as OVERLAY_CONTENT_GAP_PX,
  AGENT_SIDEBAR_OVERLAY_INSET_PX,
  AGENT_SIDEBAR_OVERLAY_RIGHT_PX as OVERLAY_RIGHT_PX,
  AGENT_SIDEBAR_WIDTH_PX,
  COLLAPSED_RAIL_BUTTON_SIZE_PX,
} from "src/components/layout/page-sidebar/agent/constants";
import { ReportPageRailContext } from "src/components/page-rail";
import {
  CRUMB_BAR_MIN_HEIGHT,
  PAGE_CARD_HEADER_PADDING_Y,
  TITLE_ROW_MIN_HEIGHT,
} from "src/components/layout/page-card-metrics";

/** Beside a crumb bar, 12px puts the body's 32px header row level with it, close button included. */
const PANEL_TOP_BESIDE_CRUMB_BAR = 3;
/** With the bar folded away, the row centres on the title row instead, lower by the header's padding and half the rows' height difference. */
const PANEL_TOP_BESIDE_TITLE_ROW =
  PANEL_TOP_BESIDE_CRUMB_BAR +
  PAGE_CARD_HEADER_PADDING_Y +
  (TITLE_ROW_MIN_HEIGHT - CRUMB_BAR_MIN_HEIGHT) / 2;
/** Beside a floating pill, 20px puts the body's 32px header row level with the pill's controls: the pill's 16px inset, then its 4px padding. */
const PANEL_TOP_BESIDE_PILL = 5;

// The shimmering text's color, under the shimmer's lighter copy of the icon.
const WORKING_ICON_STYLES = { svg: { color: "text.tertiary" } } as const;

// As the canvas's tool toggles draw pressed. The tertiary variant colors its icon through an `svg` rule, so the pressed look restates it there.
// The canvas pill's open tint, which the comments toggle beside it shares through `HeaderTogglePill`'s `openStyle="tint"`.
const PILL_TOGGLE_OPEN_STYLES = {
  bg: "primary.background",
  svg: { color: "primary.base" },
  _hover: { bg: "primary.backgroundHover", svg: { color: "primary.hover" } },
  // htui rings every expanded button, which a click would leave on for as long as the panel is open; the ring belongs to keyboard focus.
  boxShadow: "none",
  _focusVisible: { boxShadow: "outline" },
} as const;

const OVERLAY_WIDTH_PX =
  AGENT_SIDEBAR_WIDTH_PX - OVERLAY_CONTENT_GAP_PX + CLIP_PADDING_PX;

/** What Tab can reach inside the overlaid panel. */
const TABBABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(", ");

/** Keeps Tab inside a panel over a scrim, wrapping at its ends; a menu or popover it opened handles its own keys. */
const keepTabInside = (event: KeyboardEvent, panel: HTMLElement) => {
  const active = document.activeElement;
  const portal = active?.closest(".chakra-portal");
  if (portal && !portal.contains(panel)) return;
  const tabbables = Array.from(
    panel.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR),
  ).filter(
    (element) =>
      element.tabIndex >= 0 &&
      (element.checkVisibility?.({ visibilityProperty: true }) ?? true),
  );
  const first = tabbables[0];
  const last = tabbables.at(-1);
  const isLeaving =
    !active ||
    !panel.contains(active) ||
    (event.shiftKey ? active === first || active === panel : active === last);
  if (!isLeaving) return;
  event.preventDefault();
  ((event.shiftKey ? last : first) ?? panel).focus();
};

const hasHotkey = <TPanel extends string>(
  panel: AgentSidebarPanel<TPanel>,
): panel is AgentSidebarPanel<TPanel> & { hotkey: string } =>
  Boolean(panel.hotkey);

const IDLE_ACTIVITY: AgentActivity = {
  hasUnreadResponse: false,
  isAgentBusy: false,
  isAwaitingReply: false,
};

/** One pane of the sidebar: a collapsed-rail button plus what it opens. */
export type AgentSidebarPanel<TPanel extends string> = {
  id: TPanel;
  icon: ComponentType<SVGAttributes<SVGElement>>;
  /**
   * The rail button's accessible name and idle tooltip.
   * Use sentence case so the label can stand alone.
   */
  label: string;
  /** Lowercase, for the collapse control's "Collapse <name> panel", e.g. "insights". */
  name: string;
  /** react-hotkeys-hook combo (e.g. "shift+a") that toggles this panel. */
  hotkey?: string;
  isDisabled?: boolean;
  /** Collapsed-rail tooltip while disabled, e.g. a permission explanation. */
  disabledLabel?: string;
  /**
   * The panel that hosts the chat: its rail button badges with agent
   * activity (pulsing while a chat runs, solid once a reply is unread) and
   * its tooltip says so.
   */
  showsAgentActivity?: boolean;
};

export type AgentSidebarVariant = "page" | "wizard";

/** What the shell hands the host to render the sidebar body. */
export type AgentSidebarBodyProps<TPanel extends string> = Pick<
  AgentSidebarState<TPanel>,
  "isOpen" | "panel" | "selectPanel" | "setOpenTemporarily" | "openTemporarily"
> & {
  /**
   * Whether the rail sits beside the page card (true) or floats over it as
   * an overlay panel (false). Docked, a body's header controls can sit flush
   * to the rail's edge to line up with the collapsed rail's buttons. Overlaid
   * there is no rail to line up with, so they share the body's gutter.
   */
  isDocked: boolean;
  /** `?agentSession=` at mount, for the body to open that chat. */
  initialSessionId: string | null;
  /** Mirrors the open chat into `?agentSession=`. */
  onSessionChange: (sessionId: string | null) => void;
  /** Reports activity so the collapsed rail can badge the chat panel's icon. */
  onAgentActivityChange: (activity: AgentActivity) => void;
  /** The collapse control, wired to this sidebar, for the body's header. */
  collapseButton: ReactNode;
};

type Props<TPanel extends string> = {
  enabled: boolean;
  storageKey: string;
  defaultPanel: TPanel;
  panels: ReadonlyArray<AgentSidebarPanel<TPanel>>;
  /** Accessible name for the `<aside>`. */
  name: SidebarName;
  /**
   * "page" (default) docks beside the page card and overlays below xl.
   * "wizard" fills the grey region between the header and action bar, overlaying below xl.
   */
  variant?: AgentSidebarVariant;
  /** Keeps the sidebar beside a companion panel at every viewport width. */
  forceDocked?: boolean;
  /**
   * Holds the sidebar open on this panel: no collapse control, hotkeys, or
   * overlay dismissal. Releasing it restores the saved open state.
   */
  lockedPanel?: TPanel;
  /** The host's own view in the panel leaves no way to close it, as the calendar's definitions editor does. */
  hideCollapseButton?: boolean;
  /** Collapses the sidebar unless the person opened it, for a page with its own composer. */
  collapseByDefault?: boolean;
  /** Renders the sidebar body. Kept mounted while collapsed. */
  children: (body: AgentSidebarBodyProps<TPanel>) => ReactNode;
  /** Reports how far the sidebar reaches in from the viewport's right edge, so a drawer can open beside it. */
  onRightInsetChange?: (rightInsetPx: number) => void;
};

/**
 * Hosts panel content and a collapsed icon rail in the page or wizard sidebar slot.
 * Owns visibility, panel selection, and responsive state; hosts supply configuration and content.
 */
export function AgentSidebar<TPanel extends string>({
  enabled,
  storageKey,
  defaultPanel,
  panels,
  name,
  variant = "page",
  forceDocked = false,
  lockedPanel,
  hideCollapseButton = false,
  collapseByDefault,
  children,
  onRightInsetChange,
}: Props<TPanel>) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const agentSessionParam = searchParams.get(AGENT_SESSION_PARAM);
  const isWideScreen = useIsScreenAtLeast(AGENT_SIDEBAR_DOCK_BREAKPOINT);
  const isDockable = forceDocked || isWideScreen;

  const {
    isOpen: isOpenByState,
    panel: panelByState,
    selectPanel,
    setOpenTemporarily,
    openTemporarily,
    openAndRemember,
    closeAndRemember,
  } = useAgentSidebarState<TPanel>({
    autoOpen: isDockable,
    isDockable,
    enabled,
    storageKey,
    agentSessionParam,
    defaultPanel,
    collapseByDefault,
  });
  const isLocked = lockedPanel !== undefined;
  const isOpen = isLocked || isOpenByState;
  const panel = lockedPanel ?? panelByState;

  // Beside the product nav the panel mirrors its sidebar: closed, it's gone, and its toggle sits at the crumb bar's right end.
  const { slot: toggleSlot, placement: togglePlacement } = useContext(
    CrumbBarEndSlotContext,
  );
  const isMirrored = variant === "page" && toggleSlot !== null;
  const isBesideCrumbBar = isMirrored && togglePlacement === "crumbBar";
  const isInPill = isMirrored && togglePlacement === "pill";
  // A pill floats over the page's edge, so the panel widens the rail instead of folding over the page, and the pill rides that edge rather than going under the panel.
  const slidesAsFold = isMirrored && !isInPill;
  const isNavCollapsed = useIsMiniNavCollapsed();
  // Small only while the collapsed sidebar's toggle leads the crumb bar, peeked or not, so the bar reads as one toolbar.
  const isCrumbBarSize = isBesideCrumbBar && isNavCollapsed;
  // Docked, opening slides the panel in over the page and commits the layout on its last frame.
  // Closing commits it first and slides the panel out from its old spot, as the sidebar folds.
  const [foldState, setFold] = useState<{
    kind: "opening" | "closing";
    top: number;
    bottom: number;
  } | null>(null);
  // A fold the panel's state has moved on from, by a resize below the dock width or the body closing it, no longer runs.
  const isFoldOpening = foldState?.kind === "opening";
  const fold =
    foldState && isDockable && isFoldOpening === isOpen ? foldState : null;

  const { headerHeight } = useHeaderHeight();

  const isWizard = variant === "wizard";
  // The wizard's fullscreen drawer covers the app header; the rail hangs
  // below the wizard's own 64px header instead.
  const topOffsetPx = isWizard ? WIZARD_HEADER_SIZE_PX : headerHeight;
  // Overlaid, the panel keeps to the view the page fills, such as settings' inset panel; a wizard's drawer covers the viewport.
  const viewInset = useViewInset();
  const overlayInset = isWizard ? "0px" : viewInset;
  const overlayMaxWidth = `calc(100vw - 2 * ${overlayInset} - ${AGENT_SIDEBAR_OVERLAY_INSET_PX + OVERLAY_RIGHT_PX}px)`;

  const [agentActivity, setAgentActivity] =
    useState<AgentActivity>(IDLE_ACTIVITY);

  const handleSessionChange = useCallback(
    (sessionId: string | null) => {
      const next = withAgentSessionParam(searchParams, sessionId);
      if (next) {
        setSearchParams(next, { replace: true, state: location.state });
      }
    },
    [searchParams, setSearchParams, location.state],
  );

  const isExpandedInPlace = isOpen && isDockable && fold?.kind !== "opening";
  const isOverlaid = isOpen && !isDockable;
  const shouldReduceMotion = useReducedMotion() ?? false;
  // Mirrored and closed, the panel takes no room beside the page.
  const isGone = isMirrored && !isExpandedInPlace;

  const reportPageRail = useContext(ReportPageRailContext);
  // Layout effects so the card's gutter matches the rail before the first paint.
  useLayoutEffect(() => {
    if (!isWizard) reportPageRail?.(!isGone);
  }, [reportPageRail, isGone, isWizard]);
  useLayoutEffect(() => () => reportPageRail?.(null), [reportPageRail]);

  // An opening fold reports the room the panel is about to take, so a drawer opens beside it rather than under it.
  const isTakingRoom = isExpandedInPlace || fold?.kind === "opening";
  const rightInsetPx = isOverlaid
    ? OVERLAY_RIGHT_PX + OVERLAY_WIDTH_PX
    : isGone && !isTakingRoom
      ? 0
      : (isTakingRoom
          ? AGENT_SIDEBAR_WIDTH_PX
          : COLLAPSED_RAIL_BUTTON_SIZE_PX) +
        AGENT_SIDEBAR_EDGE_GAP_PX * (isWizard ? 2 : 1);
  // Layout effect so a drawer that mounts with the page gets the inset before the first paint.
  useLayoutEffect(() => {
    onRightInsetChange?.(rightInsetPx);
  }, [onRightInsetChange, rightInsetPx]);

  // Focus contract for the expand/collapse disclosure: opening moves focus
  // into the panel (the trigger strip display-nones away, so focus must not be
  // stranded on a hidden button); closing returns it to the trigger for
  // whichever pane was open. Pointer dismissal (outside click) leaves focus
  // where the click landed.
  const overlayPanelRef = useRef<HTMLDivElement | null>(null);
  const triggerRefs = useRef(new Map<TPanel, HTMLButtonElement>());
  // One callback ref per panel, created once, so React does not detach and
  // re-attach every trigger on each render.
  const triggerRefCallbacks = useRef(
    new Map<TPanel, (node: HTMLButtonElement | null) => void>(),
  );
  const getTriggerRef = useCallback((id: TPanel) => {
    let callback = triggerRefCallbacks.current.get(id);
    if (!callback) {
      callback = (node: HTMLButtonElement | null) => {
        if (node) {
          triggerRefs.current.set(id, node);
        } else {
          triggerRefs.current.delete(id);
        }
      };
      triggerRefCallbacks.current.set(id, callback);
    }
    return callback;
  }, []);

  const focusPanel = useCallback(() => {
    requestAnimationFrame(() => overlayPanelRef.current?.focus());
  }, []);

  const focusTrigger = useCallback((which: TPanel) => {
    requestAnimationFrame(() => {
      triggerRefs.current.get(which)?.focus();
    });
  }, []);

  const startFold = useCallback(
    (kind: "opening" | "closing") => {
      // The sticky column beside the page, which the panel docks in.
      const container = overlayPanelRef.current?.parentElement;
      if (
        !slidesAsFold ||
        !isDockable ||
        shouldReduceMotion ||
        !container ||
        hasTransformedAncestor(container)
      ) {
        return;
      }
      const rect = container.getBoundingClientRect();
      setFold({
        kind,
        top: rect.top,
        bottom: Math.max(0, window.innerHeight - rect.bottom),
      });
    },
    [slidesAsFold, isDockable, shouldReduceMotion],
  );

  const openPanel = useCallback(
    (which: TPanel) => {
      // Like the sidebar, a fold runs to its end before the next one.
      if (fold) return;
      if (isOpen) selectPanel(which);
      else {
        startFold("opening");
        openAndRemember(which);
      }
      focusPanel();
    },
    [fold, isOpen, selectPanel, openAndRemember, focusPanel, startFold],
  );

  const collapsePanel = useCallback(() => {
    if (fold) return;
    if (isOpen) startFold("closing");
    closeAndRemember();
    focusTrigger(panel);
  }, [fold, isOpen, startFold, closeAndRemember, panel, focusTrigger]);

  // The fold's slide, on the nav's own timing; once it ends the panel already sits where the layout puts it.
  const foldKind = fold?.kind;
  useLayoutEffect(() => {
    const panelElement = overlayPanelRef.current;
    if (!foldKind || !panelElement) return;
    const isOpening = foldKind === "opening";
    const offscreen = `translateX(calc(100% + ${AGENT_SIDEBAR_EDGE_GAP_PX}px))`;
    const slide = panelElement.animate(
      [
        { transform: isOpening ? offscreen : "translateX(0)" },
        { transform: isOpening ? "translateX(0)" : offscreen },
      ],
      {
        duration:
          (isOpening ? navMotion.unfoldDuration : navMotion.foldDuration) *
          1000,
        easing: `cubic-bezier(${navMotion.foldEase.join(", ")})`,
        fill: "forwards",
      },
    );
    let isOwnCancel = false;
    // Any end but this effect's own cancel still owes the fold its end.
    slide.finished.then(
      () => setFold(null),
      () => {
        if (!isOwnCancel) setFold(null);
      },
    );
    return () => {
      isOwnCancel = true;
      slide.cancel();
    };
  }, [foldKind]);

  // Overlays are dismissible with Escape or by clicking back into the page, or on the scrim the product nav's overlay draws.
  // Over that scrim the page can't be used, so Tab stays in the panel; without one the page stays interactive underneath.
  const hasScrim = isMirrored && isOverlaid;
  useEffect(() => {
    if (!isOverlaid || isLocked) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        collapsePanel();
      }
      const panelElement = overlayPanelRef.current;
      if (event.key === "Tab" && hasScrim && panelElement) {
        keepTabInside(event, panelElement);
      }
    };

    // Capture phase preserves dismissal when page controls stop propagation.
    // Popup portals count as inside, but the wizard portal containing the sidebar does not.
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      const sidebar = overlayPanelRef.current;
      if (!(target instanceof Element) || !sidebar) return;
      if (sidebar.contains(target)) return;
      // A pill's toggle stays mounted while the panel is open and closes it on click; dismissing on its press would let that click reopen it.
      for (const trigger of triggerRefs.current.values()) {
        if (trigger.contains(target)) return;
      }
      const portal = target.closest(".chakra-portal");
      if (portal && !portal.contains(sidebar)) return;
      closeAndRemember();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [isOverlaid, isLocked, hasScrim, closeAndRemember, collapsePanel]);

  const togglePanelFromHotkey = useCallback(
    (target: AgentSidebarPanel<TPanel>) => {
      if (isOpen && panel === target.id) {
        collapsePanel();
      } else {
        openPanel(target.id);
      }
    },
    [isOpen, panel, collapsePanel, openPanel],
  );

  const panelPaddingTop = isBesideCrumbBar
    ? PANEL_TOP_BESIDE_CRUMB_BAR
    : isInPill
      ? PANEL_TOP_BESIDE_PILL
      : isMirrored
        ? PANEL_TOP_BESIDE_TITLE_ROW
        : 4;

  // Hosts show their first panel in place of a selected one they have since dropped.
  const activePanel = panels.find((target) => target.id === panel) ?? panels[0];
  const collapseButton =
    isLocked || hideCollapseButton || !activePanel ? null : (
      <AgentSidebarCollapseButton
        panelName={activePanel.name}
        isCrumbBarSize={isCrumbBarSize}
        isOpen={isOpen}
        onCollapse={collapsePanel}
        keyboardShortcut={activePanel.hotkey}
      />
    );

  return (
    <PageSidebarContainer
      name={name}
      hideRightBorder
      top={0}
      pt={panelPaddingTop}
      // The sticky container is a z-auto stacking context, so page-card
      // content with any z-index (the query builder's toolbar uses 2) paints
      // over everything in the rail, including the fixed overlay panel,
      // whose own z-index can't escape this context. "docked" lifts the
      // rail above card content while staying far below portalled menus,
      // popovers, and modals.
      zIndex="docked"
      // When not in the wizard, mirror the card→rail gap on the rail→viewport
      // side so the rail's content sits equally far from both. The rail clips
      // while expanded, so part of that gap is padding inside the clip and the
      // rest is margin. In the wizard, we use padding because we need the
      // background color to extend to the edges.
      {...(isWizard
        ? {
            pl: `${AGENT_SIDEBAR_EDGE_GAP_PX}px`,
            pr: `${AGENT_SIDEBAR_EDGE_GAP_PX}px`,
            mr: 0,
          }
        : {
            pl: 0,
            pr: `${CLIP_PADDING_PX}px`,
            mr: `${AGENT_SIDEBAR_EDGE_GAP_PX - CLIP_PADDING_PX}px`,
          })}
      alignItems={isExpandedInPlace ? "auto" : "center"}
      // Mirrored and closed, it keeps its panel mounted at no width, with no gap beside the page.
      {...(isGone && { pr: 0, mr: 0 })}
      // The width is either the button size or the full sidebar width, depending
      // on whether we're expanded in place or not, plus the horizontal padding
      // defined above because we're `box-sizing: border-box`.
      width={
        isGone
          ? 0
          : `${
              (isExpandedInPlace
                ? AGENT_SIDEBAR_WIDTH_PX
                : COLLAPSED_RAIL_BUTTON_SIZE_PX) +
              (isWizard ? AGENT_SIDEBAR_EDGE_GAP_PX * 2 : CLIP_PADDING_PX)
            }px`
      }
      // Docked, expanding and collapsing animates the rail's width so the
      // page card resizes with it instead of jumping ~350px in a single
      // frame. Width is a layout property, which we normally avoid animating,
      // but a docked rail's sibling card genuinely has to reflow, so there
      // is nothing cheaper to animate here. Both children are fixed-width
      // and right-anchored (alignSelf flex-end), so this clip reveals them in
      // place rather than reflowing their text every frame. The panel needs
      // that anchor so its overflow goes left while the rail widens: overflow
      // past the right edge grows the page a horizontal scrollbar in Firefox.
      // The clip applies only while expanded: collapsed, nothing overflows the
      // one-button-wide rail, so there is nothing to clip.
      {...(isDockable && {
        overflow: isExpandedInPlace ? "clip" : undefined,
      })}
      // Mirrored, the panel slides on its own, as the sidebar folds; the rail's width transition is the classic nav's, and a pill's.
      {...(isDockable &&
        !slidesAsFold && {
          transitionProperty: "width",
          transitionDuration: shouldReduceMotion ? "0ms" : "normal",
          transitionTimingFunction: "easeOutQuad",
        })}
      // The wizard rail fills the region between its sticky header and action bar.
      {...(isWizard && {
        top: `${WIZARD_HEADER_SIZE_PX}px`,
        alignSelf: "flex-start",
        ignoreHeaderHeight: true,
        height: `calc(100vh - ${WIZARD_HEADER_SIZE_PX + BOTTOM_BAR_HEIGHT_PX}px)`,
        bg: "base.background",
        // Stay under the content shadow when docked and above the builder toolbar when overlaid.
        zIndex: isOverlaid ? "docked" : 0,
      })}
    >
      {panels.map((target) =>
        hasHotkey(target) && !target.isDisabled && !isLocked ? (
          <PanelHotkey
            key={target.id}
            panel={target}
            onToggle={togglePanelFromHotkey}
          />
        ) : null,
      )}
      {isMirrored ? (
        toggleSlot &&
        // A pill keeps its toggle while the panel is open, so the pill holds its width and the toggle closes what it opened.
        (!isOpen || (isInPill && !isLocked)) &&
        createPortal(
          // The panel's own toggle goes last, at the spot the open panel's close button takes.
          panels
            .toSorted(
              (a, b) =>
                Number(Boolean(a.showsAgentActivity)) -
                Number(Boolean(b.showsAgentActivity)),
            )
            .map((target) => {
              const isTargetOpen = isOpen && panel === target.id;
              return (
                <CrumbBarPanelToggle
                  key={target.id}
                  isCrumbBarSize={isCrumbBarSize}
                  isInPill={isInPill}
                  isOpen={isTargetOpen}
                  panel={target}
                  // Open, the chat itself shows its work, so the toggle drops the marker.
                  activity={
                    target.showsAgentActivity && !isTargetOpen
                      ? agentActivity
                      : null
                  }
                  buttonRef={getTriggerRef(target.id)}
                  onOpen={() => openPanel(target.id)}
                  onCollapse={collapsePanel}
                />
              );
            }),
          toggleSlot,
        )
      ) : (
        <Column
          gap={2}
          flexShrink={0}
          align="center"
          alignSelf="flex-end"
          // This column remains mounted while the panel is open so that we have
          // a mounted trigger to hand focus back to when dismissing the overlay.
          display={isOpen ? "none" : "flex"}
        >
          {panels.map((target) => {
            const isDisabled = Boolean(target.isDisabled);
            const activity = target.showsAgentActivity ? agentActivity : null;
            const activityLabel = agentActivityLabel(activity);
            return (
              <Box key={target.id} position="relative">
                <CollapsedRailButton
                  // Hide the tooltip instantly when this panel is `display: none`
                  // so that it doesn't flash a render elsewhere on the page.
                  disableTooltip={isOpen}
                  buttonRef={getTriggerRef(target.id)}
                  icon={target.icon}
                  label={
                    isDisabled
                      ? (target.disabledLabel ?? target.label)
                      : (activityLabel ?? target.label)
                  }
                  aria-label={target.label}
                  keyboardShortcut={target.hotkey}
                  isExpanded={isOpen}
                  isDisabled={isDisabled}
                  isWorking={Boolean(activity?.isAgentBusy)}
                  onClick={() => openPanel(target.id)}
                />
                {/* Collapsed, this rail is the only surface that can tell the
                  user a chat is running or a reply is waiting. A shimmering
                  icon = in flight, solid dot = unread response, amber ring =
                  the open chat waits on them. */}
                <AgentActivityMarker activity={activity} />
              </Box>
            );
          })}
        </Column>
      )}
      {isMirrored && !isDockable && (
        // Overlaid, the panel sits over a scrim, as the sidebar's sheet does; a press on it closes the panel.
        <Box
          aria-hidden
          position="fixed"
          inset={0}
          bg="blackAlpha.40"
          zIndex="docked"
          opacity={isOverlaid ? 1 : 0}
          pointerEvents={isOverlaid ? "auto" : "none"}
          transitionProperty="opacity"
          transitionDuration={
            shouldReduceMotion ? "0ms" : isOverlaid ? "normal" : "fast"
          }
          transitionTimingFunction="easeOutCubic"
        />
      )}
      {/* Kept mounted while collapsed so chat state and the page's
          agent-nudge listeners survive collapse. The docked/overlaid switch
          is styling only, for the same reason: remounting here would drop
          an in-flight conversation. */}
      <Column
        ref={overlayPanelRef}
        // Programmatically focusable: opening moves focus here, since the
        // trigger strip the click came from display-nones away.
        tabIndex={-1}
        {...(isOverlaid &&
          !isLocked && {
            role: "dialog",
            "aria-modal": hasScrim,
            "aria-label": "Agent panel",
          })}
        outline="none"
        _focusVisible={{ boxShadow: "outline" }}
        // Docked, visibility is a plain display toggle, but for a fold's slide at a fixed spot. Overlaid, the panel
        // stays laid out and slides on `transform` so the open/close is
        // animatable on the compositor, and so it is never unmounted.
        display={isDockable && !isOpen && !fold ? "none" : "flex"}
        height="100%"
        minHeight={0}
        {...(fold
          ? {
              // Mid-fold, over the page at the spot it docks in, on the page's background.
              position: "fixed",
              top: `${fold.top}px`,
              bottom: `${fold.bottom}px`,
              right: `${AGENT_SIDEBAR_EDGE_GAP_PX}px`,
              width: `${AGENT_SIDEBAR_WIDTH_PX}px`,
              height: "auto",
              pt: panelPaddingTop,
              bg: "base.background",
              zIndex: "docked",
            }
          : isDockable
            ? { width: `${AGENT_SIDEBAR_WIDTH_PX}px`, alignSelf: "flex-end" }
            : {
                width: `${OVERLAY_WIDTH_PX}px`,
                position: "fixed",
                // Inset the content from the header and right edge, with a flush bottom.
                top: `calc(${overlayInset} + ${topOffsetPx + AGENT_SIDEBAR_OVERLAY_INSET_PX}px)`,
                // Stop above the wizard action bar so Continue stays reachable.
                bottom: isWizard ? `${BOTTOM_BAR_HEIGHT_PX}px` : overlayInset,
                // Placed from the left on a phone, where a page wider than the screen widens the fixed box but not 100vw.
                right: {
                  base: "auto",
                  md: `calc(${overlayInset} + ${OVERLAY_RIGHT_PX}px)`,
                },
                left: {
                  base: `calc(100vw - ${overlayInset} - ${OVERLAY_RIGHT_PX}px - min(${OVERLAY_WIDTH_PX}px, ${overlayMaxWidth}))`,
                  md: "auto",
                },
                maxWidth: overlayMaxWidth,
                height: "auto",
                pt: panelPaddingTop,
                pr: `${CLIP_PADDING_PX}px`,
                bg: "base.background",
                // Only the top corners round; the flush bottom stays square.
                borderTopRadius: "2xl",
                boxShadow: "lg",
                overflow: "clip",
                zIndex: "docked",
                transform: isOverlaid
                  ? "translateX(0)"
                  : `translateX(calc(100% + ${overlayInset} + ${OVERLAY_RIGHT_PX}px))`,
                // Keeps the closed panel out of the tab order and off the pointer.
                visibility: isOverlaid ? "visible" : "hidden",
                // Shown at once on open, so the focus handed to it lands; hidden only once it has slid out.
                transitionProperty: isOverlaid
                  ? "transform"
                  : "transform, visibility",
                // Exits read faster than entrances: the duration applies to the
                // state being entered, so closed (slid out) transitions on "fast".
                transitionDuration: shouldReduceMotion
                  ? "0ms"
                  : isOverlaid
                    ? "normal"
                    : "fast",
                transitionTimingFunction: isMirrored
                  ? "easeOutCubic"
                  : "easeOutQuad",
              })}
      >
        {/* Where the collapse toggle is small, the chat's other title-row buttons match it. */}
        <ChatTitleButtonSizeProvider value={isCrumbBarSize ? "sm" : undefined}>
          {children({
            isOpen,
            panel,
            selectPanel,
            setOpenTemporarily,
            openTemporarily,
            isDocked: isDockable,
            initialSessionId: agentSessionParam,
            onSessionChange: handleSessionChange,
            onAgentActivityChange: setAgentActivity,
            collapseButton,
          })}
        </ChatTitleButtonSizeProvider>
      </Column>
    </PageSidebarContainer>
  );
}

/**
 * Figma-style mode shortcut for one panel: the shortcut for the open panel
 * collapses it; for another panel it opens/switches. A component per panel
 * so the hook count stays fixed for any panel list. react-hotkeys-hook
 * ignores keystrokes in form fields by default, so typing capital letters in
 * the composer never fires these.
 */
function PanelHotkey<TPanel extends string>({
  panel,
  onToggle,
}: {
  panel: AgentSidebarPanel<TPanel> & { hotkey: string };
  onToggle: (panel: AgentSidebarPanel<TPanel>) => void;
}) {
  useHotkeys(panel.hotkey, () => onToggle(panel), [onToggle, panel]);
  return null;
}

/** A closed panel's toggle at the right end of the crumb bar, mirroring the sidebar's at the left end, or at the end of the title row while the bar is folded away. In a floating pill it stays while its panel is open, pressed. */
function CrumbBarPanelToggle<TPanel extends string>({
  panel,
  isCrumbBarSize,
  isInPill,
  isOpen,
  activity,
  buttonRef,
  onOpen,
  onCollapse,
}: {
  panel: AgentSidebarPanel<TPanel>;
  /** Beside the collapsed sidebar's toggle in the crumb bar it takes that toggle's small size; elsewhere, the page actions' size. */
  isCrumbBarSize: boolean;
  /** In a floating pill at the window's top right, whose controls are round. */
  isInPill: boolean;
  isOpen: boolean;
  /** The chat's activity, which the agent panel's toggle shows while the panel is closed. */
  activity: AgentActivity | null;
  buttonRef: (node: HTMLButtonElement | null) => void;
  onOpen: () => void;
  onCollapse: () => void;
}) {
  const isDisabled = Boolean(panel.isDisabled);
  const activityLabel = agentActivityLabel(activity);
  const label = isOpen ? `Collapse ${panel.name} panel` : panel.label;
  const shouldReduceMotion = useReducedMotion() ?? false;
  const isWorking = Boolean(activity?.isAgentBusy);
  return (
    // Flex, so the button sits in the bar's row rather than on a line of text.
    <Box position="relative" display="flex">
      <Tooltip
        message={
          isDisabled
            ? (panel.disabledLabel ?? panel.label)
            : (activityLabel ?? label)
        }
        keyboardShortcut={panel.hotkey}
        // Beside the toggle, toward the page, as the sidebar's toggle's tooltip sits; in a pill, below and ending at its edge, clear of the window's.
        placement={isInPill ? "bottom-end" : "left"}
      >
        <IconButton
          ref={buttonRef}
          aria-label={label}
          aria-expanded={isOpen}
          icon={panel.icon}
          variant="tertiary"
          {...(isCrumbBarSize && SIDEBAR_TOGGLE_PROPS)}
          {...(isInPill && {
            shape: "circle",
            _expanded: PILL_TOGGLE_OPEN_STYLES,
          })}
          isDisabled={isDisabled}
          // The tertiary variant colors its icon through an `svg` rule, so dimming it for the shimmer restates it there.
          sx={isWorking ? WORKING_ICON_STYLES : undefined}
          onClick={isOpen ? onCollapse : onOpen}
        />
      </Tooltip>
      {isWorking && !shouldReduceMotion && (
        <AgentWorkingShimmer
          icon={panel.icon}
          boxSize={isCrumbBarSize ? 4 : 5}
        />
      )}
      <AgentActivityMarker activity={activity} />
    </Box>
  );
}

function agentActivityLabel(activity: AgentActivity | null) {
  if (activity?.isAgentBusy) return "Agent is working";
  if (activity?.hasUnreadResponse) return "Agent has a response for you";
  if (activity?.isAwaitingReply) return "Agent is waiting for your reply";
  return null;
}

/** The chat list's dots: solid for an unread reply, an amber ring for an open chat that waits on the person. Work in flight shimmers the icon instead. */
function AgentActivityMarker({ activity }: { activity: AgentActivity | null }) {
  if (activity?.hasUnreadResponse) return <ActiveMarkerOverlay />;
  if (activity?.isAwaitingReply) return <AgentAwaitingReplyMarker />;
  return null;
}

/** A transformed ancestor would place a fixed panel against itself rather than the window, so the fold skips its slide there. */
function hasTransformedAncestor(element: HTMLElement) {
  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    if (getComputedStyle(ancestor).transform !== "none") return true;
  }
  return false;
}
